import { useEffect, useState, useCallback, useMemo, Fragment } from "react";
import { BadgePercent, Plus, Send, ChevronDown, ChevronRight, Search, X, Check, BarChart2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/ui-helpers";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const STATUS_COLORS = {
  active: "bg-emerald-100 text-emerald-700", scheduled: "bg-blue-100 text-blue-700",
  draft: "bg-slate-100 text-slate-600", paused: "bg-amber-100 text-amber-700",
  expired: "bg-slate-100 text-slate-400", deactivated: "bg-rose-100 text-rose-600", archived: "bg-slate-200 text-slate-500",
  pending_review: "bg-amber-100 text-amber-700",
};
const BLANK = {
  name: "", promo_type: "coupon", coupon_code: "", description: "",
  discount_type: "percentage", discount_value: 10, max_discount: "", min_order_value: 0,
  scope: "seller", usage_limit: "", per_customer_limit: "",
  priority: 0, stackable: false, start_at: "", end_at: "", status: "draft",
};
const numOrNull = (v) => (v === "" || v === null ? null : Number(v));

function StatsPanel({ data }) {
  if (!data) return <p className="text-xs text-slate-400" data-testid="sp-stats-loading">Loading stats…</p>;
  const tiles = [
    ["Redemptions", data.redemptions],
    ["Revenue", `₹${data.revenue}`],
    ["Avg discount", `₹${data.avg_discount}`],
    ["Total discount", `₹${data.total_discount}`],
    ["Customers", data.unique_customers],
  ];
  return (
    <div data-testid="sp-stats-panel">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
        {tiles.map(([label, val]) => (
          <div key={label} className="rounded-lg border border-slate-200 bg-white p-2 text-center">
            <p className="text-lg font-extrabold text-slate-800">{val}</p>
            <p className="text-[11px] text-slate-500">{label}</p>
          </div>
        ))}
      </div>
      {data.redemptions === 0
        ? <p className="mt-2 text-xs text-slate-400">No redemptions yet — share your coupon to start seeing results here.</p>
        : data.last_used && <p className="mt-2 text-[11px] text-slate-400">Last used {new Date(data.last_used).toLocaleDateString()}</p>}
    </div>
  );
}

export default function SellerPromotions() {
  const [promos, setPromos] = useState([]);
  const [settings, setSettings] = useState({ seller_promotions_enabled: true, approval: "required" });
  const [form, setForm] = useState(BLANK);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [myProducts, setMyProducts] = useState([]);
  const [catNames, setCatNames] = useState({});
  const [selProducts, setSelProducts] = useState([]); // product_ids
  const [selCategories, setSelCategories] = useState([]); // category_ids
  const [prodQuery, setProdQuery] = useState("");
  const [statsFor, setStatsFor] = useState(null);
  const [statsData, setStatsData] = useState({});
  const [stockFilter, setStockFilter] = useState("all"); // all | in | out
  const [priceMin, setPriceMin] = useState("");
  const [priceMax, setPriceMax] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);

  const load = useCallback(() => {
    api.get("/marketplace/seller/promotions").then((r) => { setPromos(r.data.promotions || []); if (r.data.settings) setSettings(r.data.settings); }).catch(() => {});
  }, []);
  useEffect(() => {
    load();
    api.get("/marketplace/seller/products").then((r) => setMyProducts(r.data.products || [])).catch(() => {});
    api.get("/marketplace/public/categories-tree").then((r) => {
      const m = {}; (r.data.categories || []).forEach((c) => { m[c.category_id] = c.name; }); setCatNames(m);
    }).catch(() => {});
  }, [load]);

  // Categories the seller actually has products in.
  const myCategories = useMemo(() => {
    const ids = [...new Set(myProducts.map((p) => p.category_id).filter(Boolean))];
    return ids.map((id) => ({ id, name: catNames[id] || id, count: myProducts.filter((p) => p.category_id === id).length }))
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [myProducts, catNames]);

  const inStock = (p) => (p.stock ?? 0) > 0 || (p.variants || []).length > 0;
  const filteredProducts = useMemo(() => {
    const q = prodQuery.trim().toLowerCase();
    const lo = priceMin === "" ? null : Number(priceMin);
    const hi = priceMax === "" ? null : Number(priceMax);
    return myProducts.filter((p) => {
      if (q && !((p.name || "").toLowerCase().includes(q) || (p.brand || "").toLowerCase().includes(q))) return false;
      if (stockFilter === "in" && !inStock(p)) return false;
      if (stockFilter === "out" && inStock(p)) return false;
      const price = Number(p.price ?? 0);
      if (lo !== null && price < lo) return false;
      if (hi !== null && price > hi) return false;
      return true;
    });
  }, [myProducts, prodQuery, stockFilter, priceMin, priceMax]);

  const toggleProduct = (pid) => setSelProducts((s) => s.includes(pid) ? s.filter((x) => x !== pid) : [...s, pid]);
  const toggleCategory = (cid) => setSelCategories((s) => s.includes(cid) ? s.filter((x) => x !== cid) : [...s, cid]);

  const resetForm = () => { setForm(BLANK); setSelProducts([]); setSelCategories([]); setProdQuery(""); setStockFilter("all"); setPriceMin(""); setPriceMax(""); setShowAdvanced(false); };

  const buildBody = () => {
    const b = { ...form };
    b.max_discount = numOrNull(form.max_discount);
    b.usage_limit = numOrNull(form.usage_limit);
    b.per_customer_limit = numOrNull(form.per_customer_limit);
    b.discount_value = Number(form.discount_value || 0);
    b.min_order_value = Number(form.min_order_value || 0);
    b.priority = Number(form.priority || 0);
    b.funding = "seller";
    b.eligible_product_ids = form.scope === "product" ? selProducts : [];
    b.eligible_category_ids = form.scope === "category" ? selCategories : [];
    if (!b.start_at) delete b.start_at;
    if (!b.end_at) delete b.end_at;
    if (b.promo_type !== "coupon") b.coupon_code = null;
    return b;
  };

  const save = async () => {
    if (!form.name) { toast.error("Give the promotion a name"); return; }
    if (form.promo_type === "coupon" && !form.coupon_code) { toast.error("Enter a coupon code"); return; }
    if (form.scope === "product" && selProducts.length === 0) { toast.error("Pick at least one of your products"); return; }
    if (form.scope === "category" && selCategories.length === 0) { toast.error("Choose at least one category"); return; }
    try {
      if (editing) { await api.patch(`/marketplace/seller/promotions/${editing}`, buildBody()); toast.success("Updated"); }
      else { await api.post("/marketplace/seller/promotions", buildBody()); toast.success("Created (draft)"); }
      resetForm(); setAdding(false); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const submit = async (id) => {
    try { const r = await api.post(`/marketplace/seller/promotions/${id}/submit`); toast.success(r.data.status === "active" ? "Promotion is now live" : "Submitted for review"); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const toggleStats = async (id) => {
    if (statsFor === id) { setStatsFor(null); return; }
    setStatsFor(id);
    if (!statsData[id]) {
      try { const r = await api.get(`/marketplace/seller/promotions/${id}/stats`); setStatsData((s) => ({ ...s, [id]: r.data })); }
      catch { toast.error("Couldn't load stats"); }
    }
  };
  const startEdit = (p) => {
    setEditing(p.promo_id); setAdding(true);
    setSelProducts(p.eligible_product_ids || []);
    setSelCategories(p.eligible_category_ids || []);
    setShowAdvanced(!!(p.max_discount || p.usage_limit || p.per_customer_limit || p.start_at || p.end_at || p.description));
    setForm({ ...BLANK, ...p, coupon_code: p.coupon_code || "", description: p.description || "",
      max_discount: p.max_discount ?? "", usage_limit: p.usage_limit ?? "", per_customer_limit: p.per_customer_limit ?? "",
      start_at: p.start_at || "", end_at: p.end_at || "" });
  };
  const openNew = () => { resetForm(); setEditing(null); setAdding((v) => !v); };
  const discountText = (p) => p.discount_type === "percentage" ? `${p.discount_value}% off`
    : p.discount_type === "fixed" ? `₹${p.discount_value} off` : p.discount_type === "free_shipping" ? "Free shipping" : "Buy X Get Y";
  const appliesText = (p) => p.scope === "product" ? `${(p.eligible_product_ids || []).length} product(s)`
    : p.scope === "category" ? `${(p.eligible_category_ids || []).length} categor${(p.eligible_category_ids || []).length === 1 ? "y" : "ies"}: ${(p.eligible_category_ids || []).map((c) => catNames[c] || "—").join(", ")}` : "All my products";

  const inputCls = "rounded-lg border border-slate-200 px-3 py-2 text-sm";

  if (!settings.seller_promotions_enabled) {
    return (
      <div data-testid="seller-promotions-page">
        <PageHeader title="My Promotions" icon={BadgePercent} subtitle="Create discounts and coupons for your own products." />
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 text-sm text-amber-700" data-testid="seller-promos-disabled">Seller promotions are currently disabled by the marketplace. Please check back later.</div>
      </div>
    );
  }

  return (
    <div data-testid="seller-promotions-page">
      <PageHeader title="My Promotions" icon={BadgePercent}
        subtitle={`Create seller-funded discounts on your own products. ${settings.approval === "required" ? "New promotions need marketplace approval before going live." : "Your promotions activate automatically."}`}
        actions={<button onClick={openNew} data-testid="add-seller-promo" className="inline-flex items-center gap-1 rounded-full brand-gradient-bg px-4 py-2 text-xs font-semibold text-white"><Plus className="h-3.5 w-3.5" /> New promotion</button>} />

      {adding && (
        <div className="mb-4 space-y-4 rounded-2xl border border-slate-200 bg-white p-4" data-testid="seller-promo-form">
          {/* Primary fields */}
          <div className="grid gap-2 sm:grid-cols-2">
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Promotion name" data-testid="sp-name" />
            <select value={form.promo_type} onChange={(e) => setForm({ ...form, promo_type: e.target.value })} className={inputCls}><option value="coupon">Coupon code</option><option value="auto">Automatic</option></select>
            {form.promo_type === "coupon" && <Input value={form.coupon_code} onChange={(e) => setForm({ ...form, coupon_code: e.target.value.toUpperCase() })} placeholder="COUPON CODE" data-testid="sp-code" />}
            <div className="flex gap-2">
              <select value={form.discount_type} onChange={(e) => setForm({ ...form, discount_type: e.target.value })} className={`${inputCls} flex-1`}><option value="percentage">Percentage %</option><option value="fixed">Fixed ₹</option><option value="bxgy">Buy X Get Y</option></select>
              <Input type="number" value={form.discount_value} onChange={(e) => setForm({ ...form, discount_value: e.target.value })} placeholder="Value" data-testid="sp-value" className="w-28" />
            </div>
          </div>

          {/* Where does this coupon apply? */}
          <div>
            <p className="mb-1 text-xs font-semibold text-slate-600">Where does this coupon apply?</p>
            <select value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value })} data-testid="sp-scope" className={`${inputCls} w-full sm:w-72`}>
              <option value="seller">All my products</option>
              <option value="product">Specific products</option>
              <option value="category">A category</option>
            </select>
          </div>

          {form.scope === "product" && (
            <div className="rounded-xl border border-slate-200 p-3" data-testid="sp-product-picker">
              <div className="mb-2 flex items-center justify-between">
                <div className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-2 py-1.5">
                  <Search className="h-3.5 w-3.5 text-slate-400" />
                  <input value={prodQuery} onChange={(e) => setProdQuery(e.target.value)} placeholder="Search your products" data-testid="sp-product-search" className="w-48 text-sm outline-none" />
                </div>
                <span className="text-xs font-medium text-slate-500" data-testid="sp-selected-count">{selProducts.length} selected</span>
              </div>
              <div className="mb-2 flex flex-wrap items-center gap-2" data-testid="sp-product-filters">
                <select value={stockFilter} onChange={(e) => setStockFilter(e.target.value)} data-testid="sp-stock-filter" className="rounded-lg border border-slate-200 px-2 py-1.5 text-xs">
                  <option value="all">All stock</option>
                  <option value="in">In stock</option>
                  <option value="out">Out of stock</option>
                </select>
                <div className="flex items-center gap-1 text-xs text-slate-400">
                  <span>₹</span>
                  <input type="number" value={priceMin} onChange={(e) => setPriceMin(e.target.value)} placeholder="Min" data-testid="sp-price-min" className="w-16 rounded-lg border border-slate-200 px-2 py-1.5 text-xs text-slate-700 outline-none" />
                  <span>–</span>
                  <input type="number" value={priceMax} onChange={(e) => setPriceMax(e.target.value)} placeholder="Max" data-testid="sp-price-max" className="w-16 rounded-lg border border-slate-200 px-2 py-1.5 text-xs text-slate-700 outline-none" />
                </div>
                {(stockFilter !== "all" || priceMin !== "" || priceMax !== "") && (
                  <button onClick={() => { setStockFilter("all"); setPriceMin(""); setPriceMax(""); }} data-testid="sp-filters-clear" className="text-xs font-medium text-slate-400 hover:text-slate-600">Clear filters</button>
                )}
                <span className="ml-auto text-xs text-slate-400">{filteredProducts.length} shown</span>
              </div>
              <div className="max-h-64 space-y-1 overflow-y-auto">
                {filteredProducts.length === 0 && <p className="py-4 text-center text-xs text-slate-400">No products found.</p>}
                {filteredProducts.map((p) => {
                  const on = selProducts.includes(p.product_id);
                  return (
                    <button key={p.product_id} onClick={() => toggleProduct(p.product_id)} data-testid={`sp-product-${p.product_id}`}
                      className={`flex w-full items-center gap-3 rounded-lg border p-2 text-left transition ${on ? "border-blue-400 bg-blue-50" : "border-slate-100 hover:bg-slate-50"}`}>
                      <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border ${on ? "border-blue-500 bg-blue-500 text-white" : "border-slate-300"}`}>{on && <Check className="h-3.5 w-3.5" />}</span>
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-slate-100">
                        {(p.images || [])[0] ? <img src={p.images[0]} alt="" className="h-full w-full object-cover" /> : <BadgePercent className="h-4 w-4 text-slate-300" />}
                      </span>
                      <span className="min-w-0 flex-1"><span className="block truncate text-sm font-medium text-slate-800">{p.name}</span>
                        <span className="text-xs text-slate-400">₹{p.price} · {catNames[p.category_id] || "—"}</span></span>
                      <span className={`shrink-0 text-xs ${(p.stock ?? 0) > 0 || (p.variants || []).length ? "text-emerald-600" : "text-rose-500"}`}>{(p.stock ?? 0) > 0 || (p.variants || []).length ? `${p.stock ?? "—"} in stock` : "Out of stock"}</span>
                    </button>
                  );
                })}
              </div>
              {selProducts.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5" data-testid="sp-selected-chips">
                  {selProducts.map((pid) => {
                    const pr = myProducts.find((x) => x.product_id === pid);
                    return <span key={pid} className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">{pr?.name || pid}<button onClick={() => toggleProduct(pid)} className="text-slate-400 hover:text-rose-500"><X className="h-3 w-3" /></button></span>;
                  })}
                </div>
              )}
            </div>
          )}

          {form.scope === "category" && (
            <div className="rounded-xl border border-slate-200 p-3" data-testid="sp-category-picker">
              <div className="mb-2 flex items-center justify-between">
                <p className="text-xs text-slate-500">Pick one or more categories — applies to all your products in them (including ones you add later).</p>
                <span className="shrink-0 text-xs font-medium text-slate-500" data-testid="sp-category-count">{selCategories.length} selected</span>
              </div>
              {myCategories.length === 0 && <p className="py-2 text-xs text-slate-400">You have no products in any category yet.</p>}
              <div className="flex flex-wrap gap-2">
                {myCategories.map((c) => {
                  const on = selCategories.includes(c.id);
                  return (
                    <button key={c.id} onClick={() => toggleCategory(c.id)} data-testid={`sp-category-${c.id}`}
                      className={`inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-semibold transition ${on ? "border-blue-500 bg-blue-50 text-blue-700" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                      {on && <Check className="h-3 w-3" />}{c.name} <span className="text-slate-400">({c.count})</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Advanced options */}
          <div className="rounded-xl border border-slate-100">
            <button onClick={() => setShowAdvanced((v) => !v)} data-testid="sp-advanced-toggle" className="flex w-full items-center gap-1 px-3 py-2 text-xs font-semibold text-slate-600">
              {showAdvanced ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />} Advanced options
            </button>
            {showAdvanced && (
              <div className="grid gap-2 border-t border-slate-100 p-3 sm:grid-cols-3" data-testid="sp-advanced">
                <Input type="number" value={form.max_discount} onChange={(e) => setForm({ ...form, max_discount: e.target.value })} placeholder="Max discount ₹ (cap)" data-testid="sp-max-discount" />
                <Input type="number" value={form.min_order_value} onChange={(e) => setForm({ ...form, min_order_value: e.target.value })} placeholder="Min order value ₹" />
                <Input type="number" value={form.usage_limit} onChange={(e) => setForm({ ...form, usage_limit: e.target.value })} placeholder="Total usage limit" data-testid="sp-usage-limit" />
                <Input type="number" value={form.per_customer_limit} onChange={(e) => setForm({ ...form, per_customer_limit: e.target.value })} placeholder="Per-customer limit" data-testid="sp-per-customer" />
                <div className="flex flex-col text-xs text-slate-500"><span>Start (optional)</span><Input type="datetime-local" value={form.start_at ? form.start_at.slice(0, 16) : ""} onChange={(e) => setForm({ ...form, start_at: e.target.value ? new Date(e.target.value).toISOString() : "" })} data-testid="sp-start" /></div>
                <div className="flex flex-col text-xs text-slate-500"><span>End (optional)</span><Input type="datetime-local" value={form.end_at ? form.end_at.slice(0, 16) : ""} onChange={(e) => setForm({ ...form, end_at: e.target.value ? new Date(e.target.value).toISOString() : "" })} data-testid="sp-end" /></div>
                <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description (shown to customers)" className="sm:col-span-3" data-testid="sp-description" />
              </div>
            )}
          </div>

          <button onClick={save} data-testid="sp-save" className="w-full rounded-lg brand-gradient-bg px-4 py-2 text-sm font-semibold text-white">{editing ? "Update promotion" : "Create promotion"}</button>
        </div>
      )}

      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white" data-testid="seller-promos-table">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500"><tr><th className="p-3">Name</th><th className="p-3">Code</th><th className="p-3">Discount</th><th className="p-3">Applies to</th><th className="p-3">Used</th><th className="p-3">Status</th><th className="p-3">Actions</th></tr></thead>
          <tbody>
            {promos.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-slate-400">No promotions yet.</td></tr>}
            {promos.map((p) => (
              <Fragment key={p.promo_id}>
              <tr className="border-t border-slate-50" data-testid={`sp-${p.promo_id}`}>
                <td className="p-3 font-medium text-slate-800">{p.name}</td>
                <td className="p-3 font-mono text-xs">{p.coupon_code || "AUTO"}</td>
                <td className="p-3">{discountText(p)}</td>
                <td className="p-3 text-xs text-slate-500">{appliesText(p)}</td>
                <td className="p-3">{p.used_count}{p.usage_limit ? `/${p.usage_limit}` : ""}</td>
                <td className="p-3"><span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_COLORS[p.display_status] || "bg-slate-100 text-slate-600"}`}>{p.display_status}</span>
                  {p.approval_status === "submitted" && <span className="ml-1 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] text-amber-700">under review</span>}
                  {p.approval_status === "rejected" && <span className="ml-1 rounded bg-rose-100 px-1.5 py-0.5 text-[10px] text-rose-700">rejected</span>}
                </td>
                <td className="p-3">
                  <div className="flex gap-1">
                    {["draft", "changes_required", "rejected"].includes(p.approval_status) || p.display_status === "draft" ? <button onClick={() => startEdit(p)} className="rounded p-1 text-slate-500 hover:bg-slate-100" data-testid={`sp-edit-${p.promo_id}`}>✎</button> : null}
                    {(p.display_status === "draft" && p.approval_status !== "submitted") && <button onClick={() => submit(p.promo_id)} className="inline-flex items-center gap-1 rounded bg-blue-50 px-2 py-1 text-xs font-semibold text-blue-600 hover:bg-blue-100" data-testid={`sp-submit-${p.promo_id}`}><Send className="h-3 w-3" /> {settings.approval === "required" ? "Submit" : "Activate"}</button>}
                    <button onClick={() => toggleStats(p.promo_id)} data-testid={`sp-stats-${p.promo_id}`} className="inline-flex items-center gap-1 rounded bg-slate-50 px-2 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-100"><BarChart2 className="h-3 w-3" /> Stats</button>
                  </div>
                </td>
              </tr>
              {statsFor === p.promo_id && (
                <tr data-testid={`sp-stats-row-${p.promo_id}`}><td colSpan={7} className="bg-slate-50 p-3">
                  <StatsPanel data={statsData[p.promo_id]} />
                </td></tr>
              )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
